import { IAIPlanLimit } from '@/common/interface';
import i18n from '@/i18n';
import { AppEvent, emitter } from '@/plugin/mitt';
import { store } from '@/store';
import { globalReset } from '@/store/actions';
import { setMustChangePassword } from '@/store/slices/authSlice';
import { fetchAIUsageRequest } from '@/store/slices/aiUsageSlice';
import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import tokenRefreshService from './tokenRefreshService';

// API Base Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

// Create axios instance
const api: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000,
    headers: {
        'Content-Type': 'application/json',
    },
    // EAA-2090: auth rides on the API's HttpOnly cookies; Django's CSRF cookie is echoed back
    // in X-CSRFToken (the API is another origin, so withXSRFToken must be forced on).
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
});

// Request interceptor (auth is sent as HttpOnly cookies, see withCredentials above)
api.interceptors.request.use(
    (config) => {
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// Send the learner's app (UI) language so the backend writes AI feedback in it (EAA-2138).
api.interceptors.request.use((config) => {
    const appLanguage = (i18n.resolvedLanguage || i18n.language || '').split('-')[0];
    if (appLanguage) {
        config.headers['Accept-Language'] = appLanguage;
    }
    return config;
});

// Submissions graded under the learner's plan; each one may use up a grading.
const GRADED_SUBMIT_URL =
    /(complete-session|upload-audio|audio\/upload|feedback\/generate|writing\/submit|submit_answer|(listening|grammar)\/sessions\/[^/]+\/complete)\/?(\?|$)/;

// Response interceptor to handle token refresh and clear Redux state on 401
api.interceptors.response.use(
    (response: AxiosResponse) => {
        // Keep the usage state fresh so the "not graded" notice shows once the quota runs out.
        const { config } = response;
        if (config.method === 'post' && GRADED_SUBMIT_URL.test(config.url || '') && store.getState().aiUsage.usage) {
            store.dispatch(fetchAIUsageRequest());
        }
        return response;
    },
    async (error: AxiosError) => {
        const errorData = error.response?.data as { code?: string } | undefined;

        if (error.response?.status === 403 && errorData?.code === 'ERROR_MUST_CHANGE_PASSWORD') {
            store.dispatch(setMustChangePassword(true));
            if (window.location.pathname !== '/change-password-required') {
                emitter.emit(AppEvent.NAVIGATE, { to: '/change-password-required' });
            }
            return Promise.reject(error);
        }

        const isAIFeatureNotInPlan = error.response?.status === 403 && errorData?.code === 'AI_FEATURE_NOT_IN_PLAN';
        const isAIQuotaExceeded = error.response?.status === 429 && errorData?.code === 'AI_QUOTA_EXCEEDED';
        if (isAIFeatureNotInPlan || isAIQuotaExceeded) {
            emitter.emit(AppEvent.AI_PLAN_LIMIT, error.response?.data as IAIPlanLimit);
            return Promise.reject(error);
        }

        if (error.response?.status === 401) {
            try {
                // Try to refresh the token
                const response = await tokenRefreshService.handleTokenRefresh(error);
                return response;
            } catch (refreshError) {
                // If refresh fails, clear all Redux state using global reset
                store.dispatch(globalReset());

                // Redirect to login
                if (window.location.pathname !== '/auth/login') {
                    emitter.emit(AppEvent.NAVIGATE, { to: '/auth/login' });
                }
            }
        }
        return Promise.reject(error);
    },
);

export default api;
